import {usePrefersReducedMotion} from '@dropbox/dig-foundations';
import {type ReactNode, type RefObject, useLayoutEffect, useRef} from 'react';

const FADE_IN: Keyframe[] = [{opacity: 0}, {opacity: 1}];
const FADE_IN_TIMING: KeyframeAnimationOptions = {duration: 150, easing: 'ease-out'};

/**
 * Fades content in as it replaces a skeleton, so it doesn't pop. Pass whether
 * the skeleton is showing and put the returned ref on the content's root (or on
 * a wrapper that holds the skeleton, then the content). Only a skeleton that
 * was actually rendered counts: content that's there on first paint (e.g.
 * cached) never fades. Off under reduced motion. Pass `target` to fade an
 * element that already has a ref of its own.
 */
export const useSkeletonFadeIn = <T extends HTMLElement = HTMLDivElement>(
  loading = false,
  target?: RefObject<T>
): RefObject<T> => {
  const ownRef = useRef<T>(null);
  const ref = target ?? ownRef;
  const {hasReducedMotion} = usePrefersReducedMotion();
  const skeletonShown = useRef(false);
  useLayoutEffect(() => {
    if (loading) {
      skeletonShown.current = true;
      return;
    }
    if (!skeletonShown.current) return;
    skeletonShown.current = false;
    if (hasReducedMotion) return;
    // jsdom has no Web Animations.
    ref.current?.animate?.(FADE_IN, FADE_IN_TIMING);
    // Only the loading flip counts; a reduced-motion change shouldn't replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);
  return ref;
};

/**
 * `loading ? skeleton : children`, with the children fading in (see
 * useSkeletonFadeIn) inside a plain div. For swaps without a root element of
 * their own to hold the ref.
 */
export const SkeletonFadeIn = ({
  loading,
  skeleton,
  children,
}: {
  loading?: boolean;
  skeleton: ReactNode;
  children: ReactNode;
}) => {
  const ref = useSkeletonFadeIn(loading);
  return loading ? <>{skeleton}</> : <div ref={ref}>{children}</div>;
};
